import { useLayoutEffect, useRef, useState } from 'react'
import { useReveal } from '../../../lib/useReveal'
import { hhmmss } from '../../../lib/useFictionalClock'
import type { RegisterClass } from '../../../lib/contract'
import styles from './Traffic.module.css'
import { useT } from '../../../lib/i18n'

/** Server register → the stylesheet's class. Absent register styles as nothing. */
// ⚠ PREFIXED. The container that scrolls the transcript is also `.log`, and CSS
// Modules scope per FILE — an unprefixed `.log .text` matched every line in the
// transcript, giving the whole feed the LOG register's rule and brightness.
const REGISTER_STYLE: Record<RegisterClass, string> = {
  Voice: 'regVoice',
  Log: 'regLog',
  Protocol: 'regProtocol',
  Operator: 'regOperator',
}

/**
 * The transcript (desk-grammar brief §2).
 *
 * STYLING KEYS OFF `registerClass`, WHICH THE SERVER DERIVES — never off the
 * words (dumb-client law 2). The engine assigns Voice · Log · Protocol ·
 * Operator from its own grammar; the brief's other two classes (shorthand,
 * unit-annotation) need a generation-grammar marker the engine does not emit
 * yet, and are absent rather than guessed.
 *
 * `channel` remains a SEPARATE axis and is not folded into the register: a body
 * line classifies as Voice because it comes from the far end, but it is not
 * speech — it never crossed the wire — so it must not read as something he said.
 */
export interface TrafficRow {
  id: string
  /**
   * The engine's own message id, when this row came from a message. History is
   * hydrated from /messages AND the same session is subscribed to the live feed,
   * so the two can overlap at the seam; this is what makes the overlap a no-op
   * instead of a doubled line. Absent on desk-authored rows (seams).
   */
  msgId?: string
  /** rx = his, tx = the desk's, seam = the desk's own mark in its book. */
  kind: 'rx' | 'tx' | 'seam'
  text: string
  at?: Date | null
  /**
   * The feed's register, verbatim from the server. Optional because a running
   * engine built before the field existed simply omits it — in which case the
   * row falls back to `kind`, which is coarser but never wrong.
   */
  register?: RegisterClass
  /** The feed's channelId, verbatim. Styling reads this; never the content. */
  channel?: string
  /** First line of a burst — one generation, one burst. */
  burstStart?: boolean
  /** Outbound, not yet acknowledged by the engine. */
  pending?: boolean
  /**
   * Server-supplied reveal duration. ZERO FOR HISTORY: pacing applies only to
   * lines arriving now — scrolling up or reloading must never re-pace the log.
   */
  revealMs?: number
}

function Row({ row }: { row: TrafficRow }) {
  const t = useT()
  const text = useReveal(row.text, row.revealMs ?? 0)
  /**
   * STAMPS ARE ON DEMAND (2026-08-17, founder).
   *
   * The fictional clock runs 1:1 with real time and the design deliberately has
   * no real-time waiting — so within a contact every line is "now", and a
   * per-line seconds column emphasised a clock the product de-emphasises. What
   * carries meaning is DISCONTINUITY, and that is marked by the seams instead.
   *
   * The stamp is not deleted, only quiet: tapping a line shows its own time, and
   * the strip carries "now" ambiently. Kept because it has a second job coming —
   * §2.1's transmission lag renders here when the contract carries both times.
   */
  const [showStamp, setShowStamp] = useState(false)

  if (row.kind === 'seam') {
    /*
     * THE TIME DIVIDER. A jump, a silence, a leg that ended early — the desk
     * marking its own book. Rendered as a divider rather than a line of italics
     * because discontinuity is now the ONLY way time is stated in the feed, so
     * it has to read as a boundary rather than as a remark.
     */
    return (
      <div className={styles.seam}>
        <span className={styles.seamText}>{row.text}</span>
      </div>
    )
  }

  const classes = [
    styles.row,
    // `kind` still carries the coarse inbound/outbound split, which is layout
    // (the stamp's ›) rather than register.
    styles[row.kind],
    // The register, when the engine supplied one.
    row.register ? styles[REGISTER_STYLE[row.register]] : '',
    // Orthogonal to register: not speech, so it may not read as speech.
    row.channel === 'body' ? styles.body : '',
    row.burstStart ? styles.burst : '',
    row.pending ? styles.pending : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classes}
      onClick={() => setShowStamp((v) => !v)}
      title={row.at ? hhmmss(row.at) : undefined}
    >
      <div className={styles.text}>
        {text}
        {row.pending && (
          <span className={styles.txMark} aria-label={t('desk.traffic.transmitting')}>
            TX
          </span>
        )}
        {showStamp && row.at && (
          <span className={styles.stamp}>{hhmmss(row.at)}</span>
        )}
      </div>
    </div>
  )
}

export default function Traffic({
  rows,
  working,
  standing,
}: {
  rows: TrafficRow[]
  working: boolean
  /**
   * The dark-window standing state (§2.5) — last node relayed, stated
   * turnaround. Sourced from session data; absent when there is nothing true to
   * say, because an invented reassurance is worse than silence.
   */
  standing?: string | null
}) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = ref.current
    if (el) el.scrollTop = el.scrollHeight
  }, [rows.length, working, standing])

  return (
    <div className={styles.log} ref={ref}>
      <div className={styles.inner}>
        {rows.map((row) => (
          <Row key={row.id} row={row} />
        ))}
        {standing && <div className={styles.standing}>{standing}</div>}
        {working && <div className={styles.waiting}>— the channel is open —</div>}
      </div>
    </div>
  )
}
